import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarOff, Receipt, Paperclip, Check, X, Trash2, Lock } from "lucide-react";
import { REQ_STATUS, fmtMoney, fmtRange, fmtDay, openReceipt } from "@/lib/requests";

export function StatusChip({ status }) {
  const s = REQ_STATUS[status] || REQ_STATUS.pending;
  return <Badge variant="outline" className={`h-5 px-1.5 text-[10px] uppercase tracking-wide ${s.chip}`} data-testid={`status-${status}`}>{s.label}</Badge>;
}

function Actions({ item, me, canDecide, canClose, onAct, onCancel }) {
  const own = item.user_id === me.id;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {canDecide && item.status === "pending" && (!own || me.is_owner) && (
        <>
          <Button size="sm" onClick={() => onAct(item, "approve")} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${item.id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
          <Button size="sm" variant="outline" onClick={() => onAct(item, "reject")} className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${item.id}`}><X className="w-3.5 h-3.5 mr-1" />Reject</Button>
        </>
      )}
      {canClose && item.status === "approved" && (
        <Button size="sm" onClick={() => onAct(item, "close")} className="h-8 bg-slate-900 hover:bg-slate-800" data-testid={`close-${item.id}`}><Lock className="w-3.5 h-3.5 mr-1" />Close</Button>
      )}
      {onCancel && own && item.status === "pending" && (
        <Button size="sm" variant="ghost" onClick={() => onCancel(item)} className="h-8 text-slate-400 hover:text-red-500" data-testid={`cancel-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      )}
    </div>
  );
}

function Decision({ item }) {
  if (item.status === "closed") return <p className="text-xs text-slate-500 mt-1">Closed by {item.closed_by_name}{item.close_note ? ` — “${item.close_note}”` : ""}</p>;
  if (!item.decided_by_name) return null;
  return <p className="text-xs text-slate-500 mt-1">{REQ_STATUS[item.status]?.label} by {item.decided_by_name}{item.decision_note ? ` — “${item.decision_note}”` : ""}</p>;
}

export function LeaveRow({ item, showUser, ...rest }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`leave-row-${item.id}`}>
      <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0"><CalendarOff className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {showUser && <span className="text-sm font-semibold text-slate-900">{item.user_name}</span>}
          <span className="text-sm text-slate-700">{fmtRange(item.start_date, item.end_date)}</span>
          <span className="text-xs text-slate-400">{item.days} day{item.days > 1 ? "s" : ""}</span>
          <StatusChip status={item.status} />
        </div>
        <p className="text-xs text-slate-500 mt-1 break-words">{item.reason}</p>
        <Decision item={item} />
      </div>
      <Actions item={item} {...rest} />
    </div>
  );
}

export function ReimbRow({ item, showUser, cur, ...rest }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`reimb-row-${item.id}`}>
      <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {showUser && <span className="text-sm font-semibold text-slate-900">{item.user_name}</span>}
          <span className="text-sm font-semibold text-slate-900">{fmtMoney(item.amount, cur)}</span>
          <span className="text-xs text-slate-500">{item.category} · {fmtDay(item.expense_date)}</span>
          <StatusChip status={item.status} />
        </div>
        <p className="text-xs text-slate-500 mt-1 break-words">{item.description}</p>
        {item.receipt && (
          <button onClick={() => openReceipt(item)} className="mt-1 text-xs text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid={`receipt-${item.id}`}>
            <Paperclip className="w-3 h-3" />{item.receipt.filename}
          </button>
        )}
        <Decision item={item} />
      </div>
      <Actions item={item} {...rest} />
    </div>
  );
}
